/**
 * LiveAudioPanel - Live audio input controls (Desktop only)
 */
import React, { memo } from 'react';
import { Button } from './ui/button';
import { ChevronDown } from 'lucide-react';
import useCollapsePref from '../hooks/useCollapsePref';

const LiveAudioPanel = memo(({
  isOnMobileDevice,
  liveAudioMode,
  onToggleLiveAudio,
  showDeviceSettings,
  onToggleDeviceSettings,
  onShowSetupInstructions,
  liveInputLevel,
  audioInputDevices,
  audioOutputDevices,
  selectedInputDevice,
  onInputDeviceChange,
  selectedOutputDevice,
  onOutputDeviceChange,
  onChangeLiveDevice,
  onListAudioDevices,
  liveAudioMonitoring,
  onToggleMonitoring,
  onDeviceChangeToast,
}) => {
  const [open, toggleOpen] = useCollapsePref('live_audio', false);
  if (isOnMobileDevice) {
    return (
      <div className="space-y-2 p-3 bg-blue-500/10 rounded-lg border border-blue-400/20">
        <div className="flex items-center gap-2">
          <span className="text-xl">💻</span>
          <label className="text-white text-sm font-medium">Live Audio Input</label>
        </div>
        <p className="text-blue-200/80 text-xs">
          Live audio input is only available on desktop platforms (Windows, macOS, Linux). Mobile browsers don't support system audio capture.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3 bg-purple-500/10 rounded-lg border border-purple-400/20" data-testid="live-audio-panel">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          className="flex-1 flex items-center gap-2 text-left"
          data-testid="live-audio-collapse-toggle"
        >
          <span className="text-white text-sm font-medium">Live Audio Input</span>
          {liveAudioMode && !open && <span className="text-green-400 text-[10px] animate-pulse">● LIVE</span>}
          <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <Button
            onClick={onShowSetupInstructions}
            variant="outline"
            size="sm"
            className="h-7 px-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="live-audio-setup-guide-btn"
          >
            Full Setup Guide
          </Button>
        )}
      </div>

      <div className={open ? 'space-y-3' : 'hidden'}>
      {/* Quick Setup Instructions */}
      {!liveAudioMode && (
        <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-3 space-y-2">
          <div className="text-blue-200 text-xs font-semibold">Quick Setup Required:</div>
          <div className="text-blue-200/90 text-xs space-y-1.5">
            <div><strong>macOS:</strong> Install BlackHole 2ch &rarr; Create Multi-Output Device</div>
            <div><strong>Windows:</strong> Install VB-Cable &rarr; Set as output device</div>
            <div><strong>Linux:</strong> Use PulseAudio loopback module</div>
          </div>
          <div className="text-blue-300 text-xs mt-2 pt-2 border-t border-blue-400/20">
            Click &ldquo;Full Setup Guide&rdquo; above for detailed step-by-step instructions
          </div>
        </div>
      )}

      {/* Audio Device Selection */}
      <div className="space-y-2">
        <Button
          onClick={() => {
            onToggleDeviceSettings();
            if (!showDeviceSettings) onListAudioDevices();
          }}
          variant="outline"
          size="sm"
          className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
          data-testid="device-settings-toggle"
        >
          Audio Device Settings {showDeviceSettings ? '\u25BC' : '\u25B6'}
        </Button>

        {showDeviceSettings && (
          <div className="space-y-3 p-3 bg-black/30 rounded-lg border border-white/10">
            {/* Input Device Selector */}
            <div className="space-y-1.5">
              <label className="text-white text-xs font-medium">Input Device (Capture Audio From)</label>
              <select
                value={selectedInputDevice}
                onChange={(e) => {
                  onInputDeviceChange(e.target.value);
                  if (liveAudioMode) onDeviceChangeToast();
                }}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm"
                data-testid="input-device-select"
              >
                <option value="default">Default Input</option>
                {audioInputDevices.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Device ${device.deviceId.substring(0, 8)}...`}
                  </option>
                ))}
              </select>
              <div className="text-xs text-white/60">
                Select BlackHole/VB-Cable to capture system audio
              </div>
            </div>

            {/* Output Device Selector */}
            <div className="space-y-1.5">
              <label className="text-white text-xs font-medium">Output Device (Play Audio To)</label>
              <select
                value={selectedOutputDevice}
                onChange={async (e) => onOutputDeviceChange(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm"
                data-testid="output-device-select"
              >
                <option value="default">Default Output</option>
                {audioOutputDevices.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Device ${device.deviceId.substring(0, 8)}...`}
                  </option>
                ))}
              </select>
              <div className="text-xs text-white/60">
                Select your headphones/speakers to hear processed audio
              </div>
            </div>

            {/* Setup Instructions */}
            <div className="bg-blue-500/10 border border-blue-400/20 rounded p-2">
              <div className="text-blue-200 text-xs space-y-1">
                <div className="font-semibold">Recommended Setup:</div>
                <div>&bull; <strong>Input:</strong> BlackHole/VB-Cable</div>
                <div>&bull; <strong>Output:</strong> Your headphones/speakers</div>
                <div>&bull; Audio apps &rarr; BlackHole &rarr; NeuroWave &rarr; Speakers</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={onToggleLiveAudio}
          className={`flex-1 ${liveAudioMode ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}`}
          data-testid="live-audio-toggle"
        >
          {liveAudioMode ? 'Stop Live Input' : 'Enable Live Input'}
        </Button>

        {liveAudioMode && (
          <>
            <Button
              onClick={onChangeLiveDevice}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              title="Change input device"
            >
              Change
            </Button>
            <Button
              onClick={onToggleMonitoring}
              variant="outline"
              size="sm"
              className={`${liveAudioMonitoring ? 'bg-green-600 hover:bg-green-700' : 'bg-white/10 hover:bg-white/20'} border-white/20 text-white`}
              title={liveAudioMonitoring ? 'Monitoring ON' : 'Monitoring OFF'}
            >
              {liveAudioMonitoring ? 'Mon ON' : 'Mon OFF'}
            </Button>
          </>
        )}
      </div>

      {liveAudioMode && (
        <div className="space-y-2">
          <div className="text-xs text-purple-200 flex items-center gap-2">
            <span className="animate-pulse">&bull;</span>
            Live audio active
          </div>

          {/* Monitoring Status */}
          {liveAudioMonitoring && (
            <div className="bg-yellow-500/20 border border-yellow-400/30 rounded p-2">
              <div className="text-yellow-200 text-xs">
                <div className="font-semibold mb-1">Monitoring Enabled</div>
                <div className="text-yellow-200/80 space-y-0.5">
                  <div>&bull; Set system output to <strong>BlackHole ONLY</strong> (not Multi-Output)</div>
                  <div>&bull; NeuroWave will play the audio with 8D effects</div>
                  <div>&bull; Using Multi-Output will create feedback loops</div>
                </div>
              </div>
            </div>
          )}

          {/* Audio Level Meter */}
          <div className="space-y-1">
            <label className="text-white/70 text-xs">Audio Level</label>
            <div className="h-3 bg-black/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-100"
                style={{ width: `${liveInputLevel * 100}%` }}
              />
            </div>
            <div className="text-white/50 text-xs text-center">
              {Math.round(liveInputLevel * 100)}%
            </div>
          </div>

          {/* Active Status with Quick Tips */}
          <div className="bg-green-500/10 border border-green-400/20 rounded p-2 mt-2">
            <div className="text-green-200 text-xs">
              <div className="font-semibold mb-1">{liveAudioMonitoring ? 'Monitoring Active' : 'Capturing Audio'}</div>
              <div className="text-green-200/80 space-y-0.5">
                {liveAudioMonitoring ? (
                  <>
                    <div>&bull; You can hear live audio with 8D effects</div>
                    <div>&bull; Click Mon OFF button to mute monitoring</div>
                    <div>&bull; Make sure BlackHole is your ONLY output</div>
                  </>
                ) : (
                  <>
                    <div>&bull; Audio is being captured (meter shows input level)</div>
                    <div>&bull; Click Mon ON button to hear with 8D effects</div>
                    <div>&bull; Recording will capture audio with 8D effects</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!liveAudioMode && (
        <div className="text-xs text-purple-200/70">
          Capture system audio from DAW, Spotify, YouTube, or any app
        </div>
      )}
      </div>
    </div>
  );
});

LiveAudioPanel.displayName = 'LiveAudioPanel';

export default LiveAudioPanel;
